import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { toast } from "sonner";
import { fmtINR } from "../lib/format";
import {
    Plus, X, Edit3, Trash2, GitCompare, Loader2, AlertTriangle,
} from "lucide-react";

const STATUS_OPTS = [
    { key: "issued", label: "Issued", color: "text-[var(--brand-amber)]", bg: "bg-[var(--brand-amber)]/20" },
    { key: "cleared", label: "Cleared", color: "text-[var(--pos)]", bg: "bg-[var(--pos)]/20" },
    { key: "stopped", label: "Stopped", color: "text-[var(--text-muted)]", bg: "bg-[var(--surface)]" },
    { key: "bounced", label: "Bounced", color: "text-[var(--neg)]", bg: "bg-[var(--neg)]/20" },
    { key: "lost", label: "Lost", color: "text-[var(--text-secondary)]", bg: "bg-[var(--surface)]" },
];

const fmtDate = (s) => {
    if (!s) return "—";
    try {
        const d = new Date(s);
        return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear()).slice(-2)}`;
    } catch { return s; }
};

export default function ChequeRegister() {
    const [cheques, setCheques] = useState([]);
    const [summary, setSummary] = useState({});
    const [accounts, setAccounts] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [accountFilter, setAccountFilter] = useState("");
    const [edit, setEdit] = useState(null);
    const [busy, setBusy] = useState(false);
    const [warnings, setWarnings] = useState([]);

    const load = async () => {
        const params = [];
        if (accountFilter) params.push(`account_id=${accountFilter}`);
        if (statusFilter) params.push(`status=${statusFilter}`);
        const url = `/bank-import/cheques${params.length ? "?" + params.join("&") : ""}`;
        try {
            const r = await api.get(url);
            setCheques(r.data.cheques || []);
            setSummary(r.data.summary || {});
        } catch (e) {
            toast.error("Failed to load cheques");
        }
    };

    useEffect(() => {
        api.get("/bank-import/accounts-summary").then((r) => setAccounts(r.data.accounts || [])).catch(() => {});
    }, []);

    useEffect(() => { load(); }, [statusFilter, accountFilter]);

    const runAutoMatch = async () => {
        setBusy(true);
        try {
            const r = await api.post("/bank-import/cheques/auto-match");
            toast.success(`Matched ${r.data.matched} of ${r.data.checked_cheques} cheques`);
            setWarnings(r.data.warnings || []);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Auto-match failed");
        } finally {
            setBusy(false);
        }
    };

    const removeChq = async (id) => {
        if (!window.confirm("Delete this cheque record? This cannot be undone.")) return;
        try {
            await api.delete(`/bank-import/cheques/${id}`);
            toast.success("Cheque deleted");
            load();
        } catch (e) {
            toast.error("Failed to delete");
        }
    };

    const statusPill = (s) => {
        const opt = STATUS_OPTS.find((o) => o.key === s) || { color: "text-[var(--text-primary)]", bg: "bg-[var(--surface)]", label: s };
        return <span className={`${opt.bg} ${opt.color} px-1.5 py-0.5 font-mono-fin uppercase text-[10px]`}>{opt.label}</span>;
    };

    return (
        <div className="space-y-4" data-testid="cheque-register-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// CHEQUE-BOOK CONTROL</div>
                    <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Cheque Register</h1>
                    <div className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                        Track every issued cheque · Auto-match with bank statement debits
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        data-testid="btn-auto-match-cheques"
                        onClick={runAutoMatch}
                        disabled={busy}
                        className="flex items-center gap-1 px-3 py-1.5 border border-[var(--pos)] text-[var(--pos)] font-mono-fin text-[10px] uppercase font-bold hover:bg-[var(--pos)]/10 disabled:opacity-50"
                    >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <GitCompare size={12} />} Auto-Match to Bank
                    </button>
                    <button
                        data-testid="btn-add-cheque"
                        onClick={() => setEdit({
                            cheque_no: "", date: new Date().toISOString().slice(0, 10),
                            account_id: accounts[0]?.id || "", issued_to: "", amount: 0,
                            purpose: "", status: "issued", notes: "",
                        })}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[var(--brand-amber)] text-black font-mono-fin text-[10px] uppercase font-bold"
                    >
                        <Plus size={12} /> Issue Cheque
                    </button>
                </div>
            </div>

            {/* KPI STRIP */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <KpiCard testId="kpi-chq-total" label="TOTAL CHEQUES" value={summary.total || 0} format="raw" sub={`${cheques.length} shown`} />
                <KpiCard testId="kpi-chq-outstanding" label="OUTSTANDING" value={summary.outstanding || 0} sub="issued · not cleared" accent />
                <KpiCard testId="kpi-chq-cleared" label="CLEARED" value={summary.cleared || 0} sub="matched to bank" />
                <KpiCard testId="kpi-chq-bounced" label="BOUNCED" value={summary.bounced || 0} format="raw" sub="require action" />
                <KpiCard testId="kpi-chq-stopped" label="STOPPED / LOST" value={(summary.stopped || 0) + (summary.lost || 0)} format="raw" sub="voided" />
            </div>

            {/* WARNINGS */}
            {warnings.length > 0 && (
                <Panel title="Auto-Match Warnings · Amount matches but cheque no. not found" subtitle={`${warnings.length} suspicious debits`}>
                    <div className="space-y-2">
                        {warnings.map((w, i) => (
                            <div key={i} className="flex items-start gap-2 p-2 border border-[var(--brand-amber)]/40 bg-[var(--brand-amber)]/5" data-testid={`warning-row-${i}`}>
                                <AlertTriangle size={14} className="text-[var(--brand-amber)] mt-0.5 flex-shrink-0" />
                                <div className="flex-1 text-xs">
                                    <div className="font-mono-fin"><b className="text-[var(--brand-amber)]">Cheque {w.cheque_no}</b> · {fmtINR(w.cheque_amount)}</div>
                                    <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">
                                        Close bank debit on {fmtDate(w.close_txn_date)} · Ref: {w.close_txn_ref || "—"} · {w.close_txn_narration}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Panel>
            )}

            {/* FILTERS */}
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)] tracking-wider">FILTER:</span>
                <select
                    data-testid="filter-chq-account"
                    value={accountFilter}
                    onChange={(e) => setAccountFilter(e.target.value)}
                    className="bg-[var(--surface)] border border-[var(--border)] px-2 py-1 text-xs font-mono-fin uppercase"
                >
                    <option value="">All Accounts</option>
                    {accounts.map((a) => (
                        <option key={a.id} value={a.id}>{a.bank_name} · {a.id}</option>
                    ))}
                </select>
                <select
                    data-testid="filter-chq-status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="bg-[var(--surface)] border border-[var(--border)] px-2 py-1 text-xs font-mono-fin uppercase"
                >
                    <option value="">All Status</option>
                    {STATUS_OPTS.map((s) => (
                        <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                </select>
            </div>

            {/* TABLE */}
            <Panel title="Cheque Ledger" subtitle={`Showing ${cheques.length} record${cheques.length !== 1 ? "s" : ""}`}>
                <div className="overflow-auto max-h-[60vh]">
                    <table className="w-full text-sm">
                        <thead className="border-b border-[var(--border)] font-mono-fin text-[10px] uppercase text-[var(--text-secondary)] sticky top-0 bg-[var(--panel)] z-10">
                            <tr>
                                <th className="text-left py-2 px-2">Cheque #</th>
                                <th className="text-left px-2">Date</th>
                                <th className="text-left px-2">Account</th>
                                <th className="text-left px-2">Payee</th>
                                <th className="text-left px-2">Purpose</th>
                                <th className="text-right px-2">Amount</th>
                                <th className="text-left px-2">Status</th>
                                <th className="text-left px-2">Cleared</th>
                                <th className="text-right px-2">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cheques.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="text-center py-8 text-[var(--text-secondary)] text-xs">
                                        No cheques on record. Click "Issue Cheque" to add one.
                                    </td>
                                </tr>
                            )}
                            {cheques.map((c) => (
                                <tr key={c.id} className="border-b border-[var(--border)] hover:bg-[var(--surface)]/40" data-testid={`cheque-row-${c.id}`}>
                                    <td className="py-1.5 px-2 text-xs font-mono-fin text-[var(--brand-amber)] font-bold">{c.cheque_no}</td>
                                    <td className="px-2 text-xs font-mono-fin">{fmtDate(c.date)}</td>
                                    <td className="px-2 text-[10px] font-mono-fin text-[var(--text-secondary)]">
                                        {c.account_label || c.account_id}
                                    </td>
                                    <td className="px-2 text-xs">{c.issued_to}</td>
                                    <td className="px-2 text-[10px] text-[var(--text-secondary)] max-w-[240px] truncate" title={c.purpose}>{c.purpose || "—"}</td>
                                    <td className="px-2 text-right text-xs font-mono-fin text-[var(--neg)]">{fmtINR(c.amount)}</td>
                                    <td className="px-2">{statusPill(c.status)}</td>
                                    <td className="px-2 text-[10px] font-mono-fin text-[var(--pos)]">
                                        {c.cleared_date ? fmtDate(c.cleared_date) : "—"}
                                        {c.linked_txn_id && (
                                            <div className="text-[9px] text-[var(--text-muted)]">→ txn</div>
                                        )}
                                    </td>
                                    <td className="px-2 text-right whitespace-nowrap">
                                        <button
                                            data-testid={`btn-edit-cheque-${c.id}`}
                                            onClick={() => setEdit(c)}
                                            className="text-[var(--brand-amber)] p-1 hover:text-[var(--text-primary)]"
                                            title="Edit"
                                        >
                                            <Edit3 size={12} />
                                        </button>
                                        <button
                                            data-testid={`btn-delete-cheque-${c.id}`}
                                            onClick={() => removeChq(c.id)}
                                            className="text-[var(--neg)] p-1 hover:text-[var(--text-primary)] ml-1"
                                            title="Delete"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>

            {edit && (
                <ChequeEditModal
                    cheque={edit}
                    accounts={accounts}
                    onClose={() => setEdit(null)}
                    onDone={() => { setEdit(null); load(); }}
                />
            )}
        </div>
    );
}

function ChequeEditModal({ cheque, accounts, onClose, onDone }) {
    const [f, setF] = useState({ ...cheque });
    const isEdit = Boolean(cheque.id);

    const save = async () => {
        if (!f.cheque_no) return toast.error("Cheque number required");
        if (!f.account_id) return toast.error("Pick a bank account");
        if (!f.issued_to) return toast.error("Payee required");
        if (!f.amount || f.amount <= 0) return toast.error("Amount must be > 0");
        try {
            const body = {
                cheque_no: f.cheque_no, date: f.date, account_id: f.account_id,
                issued_to: f.issued_to, amount: Number(f.amount),
                purpose: f.purpose || "", status: f.status || "issued",
                cleared_date: f.cleared_date || null, notes: f.notes || "",
            };
            if (isEdit) {
                await api.put(`/bank-import/cheques/${cheque.id}`, body);
                toast.success(`Cheque ${f.cheque_no} updated`);
            } else {
                await api.post(`/bank-import/cheques`, body);
                toast.success(`Cheque ${f.cheque_no} issued`);
            }
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save");
        }
    };

    return (
        <div className="fixed inset-0 bg-[var(--bg-base)]/80 z-50 flex items-center justify-center p-4" data-testid="modal-cheque-edit">
            <div className="bg-[var(--panel)] border border-[var(--border)] w-full max-w-2xl">
                <div className="flex justify-between items-center p-4 border-b border-[var(--border)]">
                    <h3 className="font-display text-xl font-bold">
                        {isEdit ? `Edit Cheque ${cheque.cheque_no}` : "Issue New Cheque"}
                    </h3>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] p-1">
                        <X size={18} />
                    </button>
                </div>
                <div className="p-5 grid grid-cols-2 gap-3">
                    <Field label="Cheque Number">
                        <input data-testid="input-chq-no" value={f.cheque_no} onChange={(e) => setF({ ...f, cheque_no: e.target.value })} placeholder="e.g. 123456" className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm font-mono-fin"/>
                    </Field>
                    <Field label="Date">
                        <input data-testid="input-chq-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm font-mono-fin"/>
                    </Field>
                    <Field label="Bank Account" className="col-span-2">
                        <select data-testid="input-chq-account" value={f.account_id} onChange={(e) => setF({ ...f, account_id: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm">
                            <option value="">— pick account —</option>
                            {accounts.map((a) => (
                                <option key={a.id} value={a.id}>{a.bank_name} · {a.id} · A/C {a.account_no || "—"}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Payee (Issued To)" className="col-span-2">
                        <input data-testid="input-chq-payee" value={f.issued_to} onChange={(e) => setF({ ...f, issued_to: e.target.value })} placeholder="Vendor / Employee / Statutory dept" className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"/>
                    </Field>
                    <Field label="Amount (₹)">
                        <input data-testid="input-chq-amount" type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm font-mono-fin text-[var(--neg)]"/>
                    </Field>
                    <Field label="Status">
                        <select data-testid="input-chq-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm">
                            {STATUS_OPTS.map((s) => (
                                <option key={s.key} value={s.key}>{s.label}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Purpose" className="col-span-2">
                        <input data-testid="input-chq-purpose" value={f.purpose || ""} onChange={(e) => setF({ ...f, purpose: e.target.value })} placeholder="e.g. Retention release · Salary · Vendor payment" className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"/>
                    </Field>
                    {f.status === "cleared" && (
                        <Field label="Cleared Date" className="col-span-2">
                            <input data-testid="input-chq-cleared" type="date" value={f.cleared_date || ""} onChange={(e) => setF({ ...f, cleared_date: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm font-mono-fin"/>
                        </Field>
                    )}
                    <Field label="Notes" className="col-span-2">
                        <textarea data-testid="input-chq-notes" value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} placeholder="Optional notes" className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"/>
                    </Field>
                </div>
                <div className="flex justify-end gap-2 p-4 border-t border-[var(--border)]">
                    <button onClick={onClose} className="px-4 py-1.5 border border-[var(--border)] text-xs font-mono-fin uppercase">Cancel</button>
                    <button data-testid="btn-save-cheque" onClick={save} className="px-4 py-1.5 bg-[var(--brand-amber)] text-black text-xs font-mono-fin uppercase font-bold">
                        {isEdit ? "Update" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children, className }) {
    return (
        <div className={className}>
            <label className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)] tracking-wider mb-1 block">{label}</label>
            {children}
        </div>
    );
}
